import { debugManager } from '@tunein/web-utils';
import EventEmitter from 'eventemitter3';
import { ApsBidder } from '../amazonPublisherServicesBidder/ApsBidder';
import { MagniteBidder } from '../magniteBidder/MagniteBidder';
const debug = debugManager.createDebugger('HB', '[Web Common] Header Bidder');
export class HeaderBidder extends EventEmitter {
    static #initErrorLogger;
    static setInitErrorLogger(logger) {
        HeaderBidder.#initErrorLogger = logger;
    }
    #apsBidder;
    #magniteBidder;
    #ready = Promise.withResolvers();
    constructor(options = {}) {
        super();
        const { amazon: amazonConfig, magnite: magniteConfig } = options;
        const logError = HeaderBidder.#initErrorLogger;
        if (amazonConfig) {
            this.#apsBidder = new ApsBidder({
                bidsFetchTimeoutMs: amazonConfig.timeoutMilliseconds,
                videoBidsFetchIntervalMinutes: amazonConfig.refreshIntervalMinutes,
                videoUnitIds: amazonConfig.unitIds,
                logError,
            });
            this.#forwardPartnerEvents(this.#apsBidder, 'amazon');
        }
        if (magniteConfig) {
            this.#magniteBidder = new MagniteBidder({
                bidsFetchTimeoutMs: magniteConfig.timeoutMilliseconds,
                videoBidsFetchIntervalMinutes: magniteConfig.refreshIntervalMinutes,
                videoUnitIds: magniteConfig.unitIds,
                logError,
            });
            this.#forwardPartnerEvents(this.#magniteBidder, 'magnite');
        }
    }
    get ready() {
        return this.#ready.promise;
    }
    get #configuredPartners() {
        const partners = [];
        if (this.#apsBidder) {
            partners.push('amazon');
        }
        if (this.#magniteBidder) {
            partners.push('magnite');
        }
        return partners;
    }
    async init({ consentData, scriptUrlOverrides, useApsIframe }) {
        const inits = [];
        const partners = this.#configuredPartners;
        if (!partners.length) {
            debug.verbose('Skipping HeaderBidder init because no partners are configured.');
            this.#ready.resolve();
            return;
        }
        debug.verbose('Initializing HeaderBidder partners.', { partners });
        if (this.#apsBidder) {
            inits.push([
                'amazon',
                this.#initApsWithBlobFallback(this.#apsBidder, consentData, scriptUrlOverrides, useApsIframe),
            ]);
        }
        if (this.#magniteBidder) {
            inits.push([
                'magnite',
                this.#magniteBidder.init({
                    consentData,
                    scriptUrlOverride: scriptUrlOverrides?.magnite,
                }),
            ]);
        }
        const results = await Promise.allSettled(inits.map(([, init]) => init));
        const failedPartners = results.flatMap((result, index) => result.status === 'rejected' ? [inits[index][0]] : []);
        if (failedPartners.length) {
            debug.verbose('HeaderBidder init settled with partner failures.', { failedPartners });
        }
        else {
            debug.verbose('HeaderBidder init settled.', { partners });
        }
        this.#ready.resolve();
    }
    updateConsent(consentData) {
        debug.verbose('Updating HeaderBidder consent.', {
            forwardedToMagnite: Boolean(this.#magniteBidder),
        });
        this.#magniteBidder?.updateConsent(consentData);
    }
    async pollAndCacheVideoBids() {
        const polls = [];
        const partners = this.#configuredPartners;
        if (!partners.length) {
            debug.verbose('Skipping HeaderBidder video polling because no partners are configured.');
            return;
        }
        debug.verbose('Polling and caching video bids.', { partners });
        if (this.#apsBidder) {
            polls.push(['amazon', this.#apsBidder.pollAndCacheVideoBids()]);
        }
        if (this.#magniteBidder) {
            polls.push(['magnite', this.#magniteBidder.pollAndCacheVideoBids()]);
        }
        const results = await Promise.allSettled(polls.map(([, poll]) => poll));
        const failedPartners = results.flatMap((result, index) => result.status === 'rejected' ? [polls[index][0]] : []);
        if (failedPartners.length) {
            debug.verbose('HeaderBidder poll settled with partner failures.', { failedPartners });
        }
        else {
            debug.verbose('HeaderBidder poll settled.', { partners });
        }
    }
    consumeVideoBidsAdParams() {
        const merged = { bidRequests: [] };
        const consumedPartners = [];
        let hasAny = false;
        if (this.#apsBidder) {
            const apsBids = this.#apsBidder.consumeVideoBidsAdParams();
            if (apsBids) {
                hasAny = true;
                consumedPartners.push('amazon');
                if (apsBids.adParams) {
                    merged.adParams = { ...merged.adParams, ...apsBids.adParams };
                }
                merged.bidRequests.push(...apsBids.bidRequests);
            }
        }
        if (this.#magniteBidder) {
            const magniteBids = this.#magniteBidder.consumeVideoBidsAdParams();
            if (magniteBids) {
                hasAny = true;
                consumedPartners.push('magnite');
                if (magniteBids.adParams) {
                    merged.adParams = { ...merged.adParams, ...magniteBids.adParams };
                }
                merged.bidRequests.push(...magniteBids.bidRequests);
            }
        }
        if (!hasAny) {
            debug.verbose('No ready HeaderBidder video bids available to consume.');
            return undefined;
        }
        debug.verbose('Consumed HeaderBidder video bids.', {
            adParamCount: Object.keys(merged.adParams || {}).length,
            bidRequestCount: merged.bidRequests.length,
            partners: consumedPartners,
        });
        return merged;
    }
    stopVideoBidsPolling() {
        const partners = this.#configuredPartners;
        if (!partners.length) {
            return;
        }
        debug.verbose('Stopping HeaderBidder video polling.', { partners });
        this.#apsBidder?.stopVideoBidsPolling();
        this.#magniteBidder?.stopVideoBidsPolling();
    }
    async #initApsWithBlobFallback(apsBidder, consentData, scriptUrlOverrides, useApsIframe) {
        const scriptUrlOverride = scriptUrlOverrides?.amazon;
        const apsUseIframe = Boolean(useApsIframe);
        await apsBidder.init({
            consentData,
            scriptUrlOverride,
            useApsIframe: apsUseIframe,
        });
        if (apsUseIframe && !ApsBidder.didInitSucceed && ApsBidder.canUseBlobFallback) {
            debug.verbose('Retrying APS init with blob-URL iframe fallback.');
            await apsBidder.init({
                consentData,
                scriptUrlOverride,
                useApsIframe: apsUseIframe,
                enableAPSIFrameBlobFallback: true,
            });
        }
    }
    #forwardPartnerEvents(bidder, partner) {
        const tag = (event) => ({ ...event, partner });
        bidder.on('bidRequested', (event) => this.emit('bidRequested', tag(event)));
        bidder.on('bidResponseReceived', (event) => this.emit('bidResponseReceived', tag(event)));
        bidder.on('bidRequestFailed', (event) => this.emit('bidRequestFailed', tag(event)));
    }
}
